import { clsx } from '@alexis/helpers/clsx';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Price from '@components/Price';

import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import {
  getCurrencyState,
  getIsRtlState,
  getLocaleState,
  getTranslationsState,
} from '@redux/selectors';

import type { FlightFareResult } from '@wegoTypes/flights/metasearch/flightFareResult';

import styles from './FareDetail.module.scss';

interface FareDetailProps {
  fare: FlightFareResultTripFare;
  flightFareResult: FlightFareResult;
  isLastFare: boolean;
  isRecommendedFareDetail?: boolean;
  passengerCount: number;
  onHide?: () => void;
}

const FareDetail: React.FC<FareDetailProps> = ({
  fare,
  flightFareResult,
  isLastFare,
  isRecommendedFareDetail,
  passengerCount,
  onHide,
}) => {
  const currency = useSelector(getCurrencyState);
  const isRtl = useSelector(getIsRtlState);
  const locale = useSelector(getLocaleState);
  const translations = useSelector(getTranslationsState);

  const { exchangeRate } = useFlightSearchResults();

  const getPaymentMethodName = useCallback(
    (id: number, paymentMethods: Array<FlightSearchResultPaymentMethod>): string => {
      const filtered = paymentMethods.filter((paymentMethod) => {
        return paymentMethod.id === id;
      });
      return filtered[0] && filtered[0].name;
    },
    [],
  );

  const getPaymentMethodImage = useCallback(
    (id: number, paymentMethods: Array<FlightSearchResultPaymentMethod>): string => {
      const name = getPaymentMethodName(id, paymentMethods);
      if (!name) {
        return '';
      }
      const sluggified = name.replace(/\s/g, '_');
      return (
        'https://res.cloudinary.com/wego/c_fit,w_26,h_13,f_auto,fl_lossy,q_auto/payment_logos/' +
        sluggified +
        '.png'
      );
    },
    [getPaymentMethodName],
  );

  return (
    <div
      className={clsx(
        styles.container,
        isLastFare && styles.lastFare,
        isRecommendedFareDetail && styles.isRecommendedFareDetail,
        isRtl && styles.rtl,
      )}
    >
      <div data-pw='tripFare_details' className={clsx(styles.fareBody)}>
        {!isRecommendedFareDetail && <div className={styles.horizontalDottedLine}></div>}

        <div className={styles.tableContainer}>
          <table data-pw='tripFare_fareBreakdownTable' className={styles.breakdown}>
            <tbody>
              <tr>
                <td>{translations.passenger_fare}</td>

                <td>
                  <div className={styles.priceAndPassenger}>
                    <Price
                      isInline
                      className={clsx(styles.price, fare.paymentFees.length > 0 && styles.hasFees)}
                      price={convertFlightSearchResultFarePriceToPrice(fare.price, 1, false, false)}
                      locale={locale}
                      currency={currency}
                      exchangeRate={exchangeRate}
                    />
                    &nbsp;x ‏{passengerCount}
                  </div>
                </td>
              </tr>

              {fare.paymentFees.length > 0 ? (
                <tr>
                  <td>
                    {translations.avg_processing_fee}

                    <br />

                    <img
                      className={styles.paymentCardLogo}
                      src={getPaymentMethodImage(
                        fare.paymentFees[0].paymentMethodId,
                        flightFareResult.paymentMethods,
                      )}
                    />

                    <span className={styles.paymentCardName}>
                      {getPaymentMethodName(
                        fare.paymentFees[0].paymentMethodId,
                        flightFareResult.paymentMethods,
                      )}
                    </span>
                  </td>

                  <td>
                    <div className={styles.priceAndPassenger}>
                      <Price
                        isInline
                        showDecimal
                        className={styles.price}
                        price={{
                          amount: fare.paymentFees[0].amount,
                          amountUsd: fare.paymentFees[0].amountUsd,
                          currencyCode: fare.paymentFees[0].currencyCode,
                        }}
                        locale={locale}
                        currency={currency}
                        exchangeRate={exchangeRate}
                      />
                      &nbsp;x ‏{passengerCount}
                    </div>
                  </td>
                </tr>
              ) : null}

              <tr className={styles.lastBeforeRow}>
                <td>{translations.passenger_number}</td>

                <td>{passengerCount}</td>
              </tr>

              <tr className={styles.total}>
                <td>
                  <span className={styles.strong}>{translations.total_price}</span>
                  <br />
                  <span className={styles.includesTaxesAndFee}>{translations.incl_taxes_fees}</span>
                </td>
                <td>
                  <span className={styles.strong}>
                    <Price
                      dataPw='tripFare_totalPrice'
                      isInline
                      className={styles.price}
                      price={convertFlightSearchResultFarePriceToPrice(
                        fare.price,
                        passengerCount,
                        true,
                        true,
                      )}
                      locale={locale}
                      currency={currency}
                      exchangeRate={exchangeRate}
                    />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>

          {fare.paymentFees.length > 0 ? (
            <div className={styles.creditFees}>
              <div className={styles.feesRow}>
                <div className={styles.feesCell}>{translations.payment_type}</div>

                <div className={styles.feesCell}>{translations.processing_fee}</div>
              </div>

              {fare.paymentFees.map((paymentFee) => {
                return (
                  <div key={paymentFee.paymentMethodId} className={styles.feesRow}>
                    <div className={styles.feesCell}>
                      <img
                        src={getPaymentMethodImage(
                          paymentFee.paymentMethodId,
                          flightFareResult.paymentMethods,
                        )}
                      />

                      {getPaymentMethodName(
                        paymentFee.paymentMethodId,
                        flightFareResult.paymentMethods,
                      )}
                    </div>

                    <div className={styles.feesCell}>
                      <Price
                        dataPw='paymentMethod_paymentFee'
                        isInline
                        showDecimal
                        className={styles.price}
                        price={{
                          amount: paymentFee.totalAmount,
                          amountUsd: paymentFee.totalAmountUsd,
                          currencyCode: paymentFee.currencyCode,
                        }}
                        locale={locale}
                        currency={currency}
                        exchangeRate={exchangeRate}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      <div className={styles.hideDetails} onClick={onHide}>
        {translations.hide_details}

        <div className={clsx(styles.chevron, styles.top)}></div>
      </div>
    </div>
  );
};

export default FareDetail;
